import { proxy } from 'valtio'
import {
  ChatApi,
  ExerciseInfo,
  LessonType,
  ProgramItem,
} from '../../shared/api/chatApi.ts'
import { SpeechService } from '../speech/speechService.ts'
import { AuthStore } from '../auth/authStore.ts'
import {
  emptyFn,
  getLast,
  startTimer,
  waitFor,
} from '../../shared/lib/utils.ts'
import { AppStore } from '../whatisnew/appStore.ts'
import { ProgressApi } from '../../shared/api/progressApi.ts'
import {
  ChatMessageWithIndex,
  IDataMessageRequest,
  isExercise,
} from '../chats/chatStore.ts'
import {
  UserTestsApi,
  StartTestResponse,
  TestQuestion,
  isTestAccessError,
} from '../../shared/api/userTestsApi.ts'
import { Payload } from '../../shared/lib/utils.ts'
import { IWrongAnswers } from '../exercises/matchWords/matchWordsStore.ts'
import { LangLevels } from '../../shared/data/onboardingSteps.ts'

type LoadingState = 'chat' | 'response' | 'record' | 'analyze' | 'command'

interface State {
  exerciseResultText?: string
  correctAnswersCount?: number | null
  wrongAnswersCount?: number | null
  wrongAnswers?: IWrongAnswers
  payload?: Payload[] | null
  lessonType: LessonType
  lastMessage: ChatMessageWithIndex
  languageLevel?: LangLevels
  userId?: number
  url?: string
  lastMessageId: number | undefined
  program?: ProgramItem
  isDemo: boolean
  isLoading: boolean
  completed: boolean
  lessonCompleted: boolean
  speakRussian: boolean
  showHistory: boolean
  showMessageTexts: boolean
  messages: ChatMessageWithIndex[]
  exercise: ChatMessageWithIndex | null
  loading?: LoadingState
  programTag: string
  nextProgramTag: string | null
  contentModal: boolean
  currentText: string
  chatId: number
  loadingMessage: ChatMessageWithIndex | undefined
  showLoadingMessage: boolean
  sendButtonDisabled: boolean
  disabledByLoading: boolean
  chatInputUnavailable: boolean
  isExercise: boolean
  currentTaskIndex: number
  inputDisabled: boolean
  showChatBlock: boolean
  showTextInput: boolean
  mute: boolean
  playingMessage?: {
    messageId: number
    progress: number
  }
  lessonRate: null | 1 | 2 | 3
  currentIndex: number
  exerciseCount: number | null
  exercises: readonly ExerciseInfo[] | null
  isLastExercise: boolean
  testData: StartTestResponse | null
  testCompleted: {
    isCompleted: boolean
    correctCount: number
    totalCount: number
  }
  statistics: {
    isLessonCompletedPage: boolean
    isStatPage: boolean
    isLoading: boolean
  }
  testAccessError: string | null
}

const defaultState = (chatId: number): State => ({
  get lastMessage() {
    return (
      this.messages[this.messages.length - 1] ?? {
        id: 1,
        message_text: '',
        text: '',
        is_ai: true,
        is_last: false,
      }
    )
  },
  get speakRussian() {
    if (this.languageLevel == 'A2') {
      return true
    }
    if (!this.program) {
      return false
    }
    return (
      this.program.level == 'A2' &&
      this.program.lesson_type == 'General conversation'
    )
  },
  mute: false,
  showTextInput: false,
  get lastMessageId() {
    return getLast(this.messages)?.id
  },
  showChatBlock: false,
  contentModal: false,
  get isDemo() {
    return false
  },
  programTag: '',
  nextProgramTag: null,
  get completed() {
    return this.program?.status == 'completed'
  },
  get lessonCompleted() {
    return !!getLast(this.messages)?.is_last
  },
  showMessageTexts: true,
  showHistory: false,
  messages: [],
  currentText: '',
  chatId,

  get loadingMessage() {
    const newMessage: ChatMessageWithIndex = {
      index: this.messages.length,
      message_text: '',
      text: '',
      is_ai: this.loading == 'response',
      id: -1,
      is_last: false,
      statePlayBtn: 'play',
    }

    return this.showLoadingMessage ? newMessage : undefined
  },

  get showLoadingMessage() {
    const showLoadingMessageStates: LoadingState[] = ['response', 'analyze']
    return this.loading
      ? showLoadingMessageStates.includes(this.loading)
      : false
  },

  get isExercise() {
    const lastMessage = getLast(this.messages)
    return isExercise(lastMessage?.content)
  },
  get chatInputUnavailable() {
    return this.isExercise || this.lessonCompleted
  },

  get disabledByLoading() {
    const completeButtonDisabledStates: LoadingState[] = [
      'response',
      'analyze',
      'command',
    ]
    return !!this.loading && completeButtonDisabledStates.includes(this.loading)
  },

  get sendButtonDisabled() {
    return this.disabledByLoading || this.chatInputUnavailable
  },
  get inputDisabled() {
    const lastMessage = getLast(this.messages)
    return this.lessonCompleted || isExercise(lastMessage?.content)
  },
  get lessonRate() {
    return getLast(this.messages)?.ai_rate?.rate ?? null
  },
  lessonType: null,
  exercise: null,
  isLoading: false,
  get isLastExercise() {
    return this.currentIndex === this.exerciseCount
  },
  currentIndex: 1,
  currentTaskIndex: 0,
  exerciseCount: null,
  get exercises() {
    const lastMessage = getLast(this.messages)
    return lastMessage?.exercises?.exercises ?? null
  },
  testData: null,
  testCompleted: {
    isCompleted: false,
    correctCount: 0,
    totalCount: 0,
  },
  statistics: {
    isLessonCompletedPage: false,
    isStatPage: false,
    isLoading: false,
  },
  testAccessError: null,
})

export class UserTestLessonStore {
  state: State

  constructor(
    private userTestsApi: UserTestsApi,
    private chatApi: ChatApi,
    private authStore: AuthStore,
    private speechService: SpeechService,
    private appStore: AppStore,
    private progressApi: ProgressApi,
  ) {
    this.state = proxy<State>(defaultState(0))
  }

  async startTest(testId: string, testType: string) {
    if (!this.authStore.state.user) {
      return
    }
    try {
      this.state.isLoading = true
      this.resetState()
      this.speechService.init()
      this.setLoading('chat')
      this.state.showChatBlock = true
      this.setLessonType(null)

      const testResponse = await this.userTestsApi.startTest(testType)
      this.state.testData = testResponse
      this.state.programTag = testResponse.program_tag
      this.state.chatId = testResponse.id

      if (testResponse.parameters && testResponse.parameters.length > 0) {
        const testQuestions: TestQuestion[] = testResponse.parameters.map(
          (param) => ({
            question: param.question,
            options: param.options,
            category: param.category,
            check: param.check,
          }),
        )

        const exerciseMessage: ChatMessageWithIndex = {
          id: 0,
          message_text: '',
          text: '',
          is_ai: true,
          is_last: false,
          index: 0,
          statePlayBtn: 'play',
          content: { testQuestions } as any,
        }

        this.state.exerciseCount = testQuestions.length
        this.state.currentIndex = 1
        this.state.exercises = testQuestions.map((q, index) => ({
          exercise_count: 1,
          type: 'match_words' as const,
        }))
        this.state.currentTaskIndex = 0

        this.setExercise(exerciseMessage)
        this.state.messages.push(exerciseMessage)
      }
    } catch (error) {
      if (isTestAccessError(error)) {
        this.state.testAccessError = (error as Error).message
      } else {
        throw error
      }
    } finally {
      this.state.isLoading = false
      this.state.loading = undefined
    }
  }

  setLoading(loading: LoadingState) {
    this.state.loading = loading
  }

  async waitForFirstMessage() {
    await waitFor(() => this.state.messages.length > 0, 100)
  }

  resetState() {
    this.state.messages = []
    this.state.exerciseCount = null
    this.state.currentIndex = 1
    this.state.testData = null
    this.state.testAccessError = null
  }

  async send(
    text: string,
    correctAnswersCount: number | null = null,
    payload: Payload[] | null = null,
    wrongAnswersCount: number | null = null,
    wrongAnswers?: IWrongAnswers,
  ) {
    if (Boolean(this.state.isLoading) || !text) {
      return
    }
    try {
      this.state.isLoading = true
      await this.waitForFirstMessage()

      const getData = () => {
        const data: IDataMessageRequest = {
          chat: this.state.chatId,
          message_text: text,
        }
        if (
          Number.isInteger(correctAnswersCount) &&
          correctAnswersCount !== null
        ) {
          data.exercise_result = {
            correct: correctAnswersCount,
            payload: null,
            wrongAnswersCount: null,
          }
          data.parent_msg = this.state.lastMessageId
        }
        if (payload !== null && data.exercise_result) {
          data.exercise_result.payload = payload
        }
        if (wrongAnswersCount !== null && data.exercise_result) {
          data.exercise_result.wrongAnswersCount = wrongAnswersCount
        }
        if (wrongAnswers && data.exercise_result) {
          data.exercise_result.wrongAnswers = wrongAnswers
        }
        return data
      }
      const timer = startTimer()
      const realUserMessageId = (await this.chatApi.message_create(getData()))
        .id

      const [newMessages] = await Promise.all([
        this.chatApi.message_process(realUserMessageId),
      ])

      for (const newMessage of newMessages) {
        void this.chatApi.patchMessage(newMessage.id, {
          duration_front: timer.stop(),
        })

        this.state.loading = undefined
        this.state.isLoading = false
      }
    } catch (error) {
      console.error(error)
    } finally {
      this.state.isLoading = false
    }
  }

  setCurrentText(text: string) {
    this.state.currentText = text
  }

  async sendCurrentText() {
    if (this.state.loading) {
      return
    }
    const text = this.state.currentText
    this.state.currentText = ''
    await this.send(text)
  }

  onExerciseComplete(
    result: string,
    correctAnswersCount?: number,
    payload?: Payload[],
    wrongAnswersCount?: number | null,
    wrongAnswers?: IWrongAnswers | null,
  ) {
    if (result.includes('Test completed')) {
      if (Number.isInteger(correctAnswersCount)) {
        this.state.correctAnswersCount = correctAnswersCount
      }
      if (Number.isInteger(wrongAnswersCount)) {
        this.state.wrongAnswersCount = wrongAnswersCount
      }
      if (wrongAnswers) {
        this.state.wrongAnswers = wrongAnswers
      }
      if (payload) {
        this.state.payload = payload
      }

      this.state.testCompleted = {
        isCompleted: true,
        correctCount: correctAnswersCount || 0,
        totalCount: this.state.exerciseCount || 0,
      }

      this.goToTestCompletedPage()
    } else {
      if (
        this.state.exerciseCount !== null &&
        this.state.currentIndex < this.state.exerciseCount
      ) {
        this.state.currentIndex++
        this.state.currentTaskIndex = 0
      }
    }
  }

  continueAfterExercise() {
    if (!this.state.exerciseResultText) {
      return
    }

    void this.send(
      this.state.exerciseResultText,
      this.state.correctAnswersCount,
      this.state.payload,
      this.state.wrongAnswersCount,
      this.state.wrongAnswers,
    )
    this.state.exerciseResultText = undefined
    this.state.correctAnswersCount = null
    this.state.wrongAnswersCount = null
    this.state.payload = null

    this.state.loading = 'chat'
    this.state.isLoading = true
  }

  setLessonType(type: LessonType) {
    this.state.lessonType = type
    this.state.mute = false
  }

  setExercise(exercise: ChatMessageWithIndex) {
    this.state.exercise = exercise
  }

  playWord(word: string) {
    void this.speechService.playText(
      undefined,
      this.state.chatId,
      word,
      emptyFn,
    )
  }

  onTaskComplete(currentIndex: number) {
    this.state.currentTaskIndex = currentIndex
  }

  stopEdmanAudio() {
    this.speechService.stopCurrentSound()
  }

  goToTestCompletedPage() {
    this.state.statistics.isLessonCompletedPage = true
  }
}
